import classNames from 'classnames'
import { useCallback } from 'react'
import { Link } from 'react-router'
import z from 'zod'
import * as styles from './ghost-button.css.ts'

export const GhostButtonTypeSchema = z.enum(['default', 'secondary', 'destructive'])
export type GhostButtonType = z.infer<typeof GhostButtonTypeSchema>
export const GhostButtonStateSchema = z.enum(['default', 'disabled'])
export type GhostButtonState = z.infer<typeof GhostButtonStateSchema>
export const GhostButtonSizeSchema = z.enum(['default', 'small', 'extra-small'])
export type GhostButtonSize = z.infer<typeof GhostButtonSizeSchema>

type GhostButtonProps = React.PropsWithChildren<{
  type?: GhostButtonType
  state?: GhostButtonState
  size?: GhostButtonSize
  href?: string
  to?: string
  className?: string
  ariaLabel?: string
  icon?: React.ReactNode
  'data-testid'?: string
  ref?: React.Ref<HTMLAnchorElement | HTMLButtonElement>
}> &
  Omit<React.HTMLAttributes<HTMLAnchorElement | HTMLButtonElement>, 'className'>

export const GhostButton: React.FC<GhostButtonProps> = ({
  children,
  type = 'default',
  state = 'default',
  size = 'default',
  href,
  to,
  className,
  ariaLabel,
  icon,
  'data-testid': testId,
  onClick,
  onKeyDown,
  ref,
  ...rest
}) => {
  const mergedClassName = classNames(styles.button({ type, state, size }), className)

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    e.preventDefault()
  }, [])

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (state === 'disabled') {
        e.preventDefault()
        return
      }
      onClick?.(e)
    },
    [state, onClick]
  )

  const handleLinkKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (state === 'disabled') {
        e.preventDefault()
        return
      }
      onKeyDown?.(e)
    },
    [state, onKeyDown]
  )

  if (to)
    return (
      <Link
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={mergedClassName}
        to={state === 'disabled' ? '#' : to}
        onMouseDown={handleMouseDown}
        onClick={handleLinkClick}
        onKeyDown={handleLinkKeyDown}
        aria-label={ariaLabel}
        data-testid={testId}
        {...(rest as React.HTMLAttributes<HTMLAnchorElement>)}
      >
        {icon}
        {children}
      </Link>
    )

  if (href)
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={mergedClassName}
        href={state === 'disabled' ? '#' : href}
        onMouseDown={handleMouseDown}
        onClick={onClick}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
        data-testid={testId}
        {...rest}
      >
        {icon}
        {children}
      </a>
    )

  // `type="button"`: without it the native default is `type="submit"`, which password managers treat
  // as a login-form submit — e.g. clicking Cancel next to credential fields prompts 1Password to
  // save. Ghost-button actions fire via onClick, never a form submit. It sits after the spread for
  // consistency, not to resolve a conflict: `rest` is `HTMLAttributes`, which declares no `type`.
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      className={mergedClassName}
      disabled={state === 'disabled'}
      onMouseDown={handleMouseDown}
      onClick={onClick}
      onKeyDown={onKeyDown}
      aria-label={ariaLabel}
      data-testid={testId}
      {...rest}
      type="button"
    >
      {icon}
      {children}
    </button>
  )
}
